// Overview tab logic shared by today's pane (OverviewTab) and the full unit
// page (UnitV2/tabs/OverviewTabV2), so the two cannot drift: the monthly
// bucketing of the marketplace (TractorHouse) and website (GA4) daily
// history, the 30-day sparkline series, and today's traffic-light rules.

/**
 * Daily history rows → monthly totals of `fields`, oldest month first.
 * Null when there is no usable row. { keys, labels, series: { field: [] }, monthCount }
 */
export function monthlyBuckets(history, fields) {
    if (!Array.isArray(history) || history.length === 0) return null;
    const buckets = new Map();
    history.forEach((row) => {
        if (!row?.view_date) return;
        const key = String(row.view_date).slice(0, 7); // YYYY-MM
        const agg = buckets.get(key) || Object.fromEntries(fields.map((f) => [f, 0]));
        fields.forEach((f) => { agg[f] += Number(row[f]) || 0; });
        buckets.set(key, agg);
    });
    const keys = [...buckets.keys()].sort();
    if (keys.length === 0) return null;
    const labels = keys.map((k) => {
        const [y, m] = k.split('-');
        return new Date(Number(y), Number(m) - 1).toLocaleDateString(undefined, { month: 'short', year: '2-digit' });
    });
    const series = Object.fromEntries(fields.map((f) => [f, keys.map((k) => buckets.get(k)[f])]));
    return { keys, labels, series, monthCount: keys.length };
}

/** The last 30 days of `field`, oldest → newest (history is newest first). */
export const last30 = (history, field) => (history || []).slice(0, 30).slice().reverse().map((d) => Number(d[field]) || 0);

/** Marketplace click-through: value (1 dp text or null) and today's tone. */
export function ctrStatus(impressions, views) {
    if (!impressions || impressions === 0) return { value: null, tone: 'poor', label: 'Very low CTR' };
    const ctr = (views / impressions) * 100;
    const value = ctr.toFixed(1);
    if (ctr >= 2) return { value, tone: 'good', label: 'Good CTR' };
    if (ctr >= 0.5) return { value, tone: 'ok', label: 'Low CTR' };
    return { value, tone: 'poor', label: 'Very low CTR' };
}

/** Email leads: good when any; poor when none after 30 days in stock. */
export const leadTone = (leads, daysInStock) => {
    if (leads > 0) return 'good';
    if (daysInStock != null && daysInStock > 30) return 'poor';
    return null;
};

/** Avg session: > 120 s good, under 30 s (and above 0) poor. */
export const sessionTone = (sec) => {
    if (sec > 120) return 'good';
    if (sec > 0 && sec < 30) return 'poor';
    return null;
};

/** Bounce rate (0–1): < 40% good, > 70% poor, otherwise amber. */
export const bounceTone = (rate) => {
    if (rate < 0.4) return 'good';
    if (rate > 0.7) return 'poor';
    return 'amber';
};

/** "—" when there is no session data at all, else the rounded %. */
export const bounceText = (rate, sessionSec) => (sessionSec === 0 && rate === 0 ? '—' : `${(rate * 100).toFixed(0)}%`);

/** Seconds as "Xm Ys" / "Xs"; "—" for none. */
export function formatSession(seconds) {
    if (!seconds || seconds <= 0) return '—';
    const s = Math.round(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rem = s % 60;
    return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
}

/** Listing completeness: ≥ 70% good, ≥ 40% warn, else poor. */
export const completenessTone = (pct) => {
    if (pct >= 70) return 'good';
    if (pct >= 40) return 'warn';
    return 'poor';
};

/**
 * A person field (Acquired From, Previous Driver): today's "Unknown" when the
 * field is missing; "—" when it is there but blank (whitespace), which used to
 * render as an empty cell (QA P-4).
 */
export const personLabel = (value) => {
    if (value == null) return 'Unknown';
    const s = String(value).trim();
    return s || '—';
};
